/**
 * FinancialsPanel - Financial data display with live data
 */

import { useEffect, useState } from 'react'
import type { CompanyData } from '../../types'
import { getFinancials, formatCurrency, formatPercent, type FinancialData } from '../../services/api'
import { PrivateCompanyBanner, PrivateCompanyNoDataMessage } from '../shared/PrivateCompanyBanner'

interface FinancialsPanelProps {
  company: CompanyData | null
  initialData?: FinancialData | null
}

interface DataFreshness {
  source: 'live' | 'cached' | 'loading' | 'error'
  timestamp?: string
  age?: string
}

const formatAge = (timestamp: string): string => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

const isDataFresh = (timestamp: string): boolean => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  return diffMs < 60000 // Within 1 minute
}

export function FinancialsPanel({ company, initialData }: FinancialsPanelProps) {
  const [financials, setFinancials] = useState<FinancialData | null>(initialData || null)
  const [freshness, setFreshness] = useState<DataFreshness>({
    source: initialData ? 'cached' : 'loading',
    timestamp: initialData ? new Date().toISOString() : undefined,
  })

  useEffect(() => {
    if (!company?.ticker) {
      setFinancials(null)
      setFreshness({ source: 'loading' })
      return
    }

    // Skip fetching for private companies - they don't have public financial data
    if (company.isPrivate) {
      setFinancials(null)
      setFreshness({ source: 'loading' })
      return
    }

    // Reset state when company changes
    setFinancials(null)
    setFreshness({ source: 'loading' })

    // Show cached/initial data immediately if available
    if (initialData) {
      setFinancials(initialData)
      setFreshness({
        source: 'cached',
        timestamp: new Date().toISOString(),
        age: 'loading fresh data...',
      })
    }

    // Fetch live data
    const fetchData = async () => {
      try {
        const result = await getFinancials(company.ticker)
        if (result.data) {
          const data = result.data as FinancialData & { timestamp?: string; dataSource?: string }
          setFinancials(result.data)
          const timestamp = data.timestamp || new Date().toISOString()
          const isFresh = isDataFresh(timestamp)
          setFreshness({
            source: isFresh ? 'live' : 'cached',
            timestamp,
            age: formatAge(timestamp),
          })
        } else if (result.error) {
          if (!initialData) {
            setFreshness({ source: 'error' })
          }
        }
      } catch (error) {
        console.error('Failed to fetch financials:', error)
        if (!initialData) {
          setFreshness({ source: 'error' })
        }
      }
    }

    fetchData()
  }, [company?.ticker, company?.isPrivate, initialData])

  if (!company) {
    return (
      <div className="flex items-center justify-center h-full text-term-textDim">
        <p>Select a company to view financial data</p>
      </div>
    )
  }

  // Handle private companies - show banner and no-data message
  const isPrivate = company.isPrivate || company.quoteType === 'PRIVATE'
  if (isPrivate) {
    return (
      <div className="space-y-6">
        <PrivateCompanyBanner companyName={company.name} />

        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-term-text">
            📊 Financial Summary - {company.ticker}
            <span className="ml-2 px-2 py-0.5 bg-term-info/30 text-term-info text-xs rounded font-bold">PRIVATE</span>
          </h2>
          <span className="text-term-textDim text-sm">
            {company.sector} │ {company.industry}
          </span>
        </div>

        <PrivateCompanyNoDataMessage
          dataType="Financial Data"
          suggestion="Request historical financial statements, management accounts, and revenue breakdowns via the Data Room."
        />

        {/* Data Room Suggestions */}
        <div className="term-box p-4">
          <h3 className="text-sm font-medium text-term-text mb-3">📋 Recommended Financial Documents</h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              'Audited Financial Statements (3-5 years)',
              'Management Accounts (YTD)',
              'Revenue Breakdown by Product/Segment',
              'Monthly P&L Statements',
              'Balance Sheet with Aging Reports',
              'Cash Flow Statements',
              'Debt Schedule & Covenants',
              'Working Capital Analysis',
            ].map((doc, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <span className="text-term-warning">○</span>
                <span className="text-term-textDim">{doc}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-term-textDim mt-4">
            💡 Request these documents via the Data Room tab to populate this analysis.
          </p>
        </div>
      </div>
    )
  }

  if (!financials && freshness.source === 'loading') {
    return (
      <div className="flex items-center justify-center h-full text-term-textDim">
        <p>Loading financial data for {company.ticker}...</p>
      </div>
    )
  }

  if (!financials && freshness.source === 'error') {
    return (
      <div className="flex items-center justify-center h-full text-term-error">
        <p>Failed to load financial data for {company.ticker}</p>
      </div>
    )
  }

  if (!financials) {
    return (
      <div className="flex items-center justify-center h-full text-term-textDim">
        <p>No financial data available for {company.ticker}</p>
      </div>
    )
  }

  // Check if this is an ETF
  const extendedFinancials = financials as FinancialData & { quoteType?: string }
  const isETF = extendedFinancials.quoteType === 'ETF'
  const isMutualFund = extendedFinancials.quoteType === 'MUTUALFUND'
  const isNonEquity = isETF || isMutualFund

  return (
    <div className="space-y-6">
      {/* ETF/Fund Warning Banner */}
      {isNonEquity && (
        <div className="bg-term-warning/20 border border-term-warning rounded-lg p-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{isETF ? '📦' : '🏦'}</span>
            <div>
              <h3 className="text-term-warning font-bold text-lg">
                {isETF ? 'Exchange-Traded Fund (ETF)' : 'Mutual Fund'}
              </h3>
              <p className="text-term-textDim text-sm">
                {isETF
                  ? 'ETFs are investment funds that trade on stock exchanges. They don\'t have traditional company financials like revenue, net income, or free cash flow. Use the FCF and Regime tabs to analyze holdings.'
                  : 'Mutual funds pool money from many investors. They don\'t have traditional company financials. Consider analyzing individual holdings instead.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header with freshness indicator */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-term-text">
          📊 Financial Summary - {company.ticker}
          {isETF && <span className="ml-2 px-2 py-0.5 bg-term-warning/30 text-term-warning text-xs rounded font-bold">ETF</span>}
          {isMutualFund && <span className="ml-2 px-2 py-0.5 bg-term-info/30 text-term-info text-xs rounded font-bold">FUND</span>}
        </h2>
        <div className="flex items-center gap-4">
          <span className="text-term-textDim text-sm">
            {company.sector} │ {company.industry}
          </span>
          <FreshnessIndicator freshness={freshness} />
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-4 gap-4">
        <MetricCard
          label="Revenue"
          value={formatCurrency(financials.revenue)}
          subValue={`${formatPercent(financials.revenueGrowth)} YoY`}
          trend={financials.revenueGrowth > 0 ? 'up' : financials.revenueGrowth < 0 ? 'down' : 'stable'}
        />
        <MetricCard
          label="Net Income"
          value={formatCurrency(financials.netIncome)}
          subValue={`${formatPercent(financials.netMargin)} margin`}
          trend={financials.netMargin > 0.15 ? 'up' : financials.netMargin > 0.05 ? 'stable' : 'down'}
        />
        <MetricCard
          label="Free Cash Flow"
          value={formatCurrency(financials.freeCashFlow)}
          subValue={`${formatPercent(financials.fcfMargin)} margin`}
          trend={financials.fcfMargin > 0.2 ? 'up' : financials.fcfMargin > 0.1 ? 'stable' : 'down'}
        />
        <MetricCard
          label="Market Cap"
          value={formatCurrency(company.marketCap)}
          subValue={`P/E: ${financials.peRatio?.toFixed(1) || 'N/A'}`}
          trend="stable"
        />
      </div>

      {/* Detailed Financials Table */}
      <div className="overflow-x-auto">
        <table className="term-table table-fixed">
          <thead>
            <tr>
              <th className="w-1/4 text-left">Metric</th>
              <th className="w-1/4 text-right">Value</th>
              <th className="w-1/4 text-right">Margin/Ratio</th>
              <th className="w-1/4 text-right">Industry Benchmark</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="w-1/4 font-medium">Revenue</td>
              <td className="w-1/4 text-right">{formatCurrency(financials.revenue)}</td>
              <td className="w-1/4 text-right text-term-success">{formatPercent(financials.revenueGrowth)} growth</td>
              <td className="w-1/4 text-right text-term-textDim">~8-12%</td>
            </tr>
            <tr>
              <td className="w-1/4 font-medium">Gross Profit</td>
              <td className="w-1/4 text-right">{formatCurrency(financials.grossProfit)}</td>
              <td className="w-1/4 text-right text-term-info">{formatPercent(financials.grossMargin)}</td>
              <td className="w-1/4 text-right text-term-textDim">~40-60%</td>
            </tr>
            <tr>
              <td className="w-1/4 font-medium">Operating Income</td>
              <td className="w-1/4 text-right">{formatCurrency(financials.operatingIncome)}</td>
              <td className="w-1/4 text-right text-term-info">{formatPercent(financials.operatingMargin)}</td>
              <td className="w-1/4 text-right text-term-textDim">~15-25%</td>
            </tr>
            <tr>
              <td className="w-1/4 font-medium">Net Income</td>
              <td className="w-1/4 text-right text-term-success">{formatCurrency(financials.netIncome)}</td>
              <td className="w-1/4 text-right text-term-info">{formatPercent(financials.netMargin)}</td>
              <td className="w-1/4 text-right text-term-textDim">~10-20%</td>
            </tr>
            <tr>
              <td className="w-1/4 font-medium">Free Cash Flow</td>
              <td className="w-1/4 text-right text-term-primary">{formatCurrency(financials.freeCashFlow)}</td>
              <td className="w-1/4 text-right text-term-info">{formatPercent(financials.fcfMargin)}</td>
              <td className="w-1/4 text-right text-term-textDim">~15-25%</td>
            </tr>
            <tr>
              <td className="w-1/4 font-medium">EPS</td>
              <td className="w-1/4 text-right">${financials.eps?.toFixed(2) || 'N/A'}</td>
              <td className="w-1/4 text-right text-term-textDim">P/E: {financials.peRatio?.toFixed(1) || 'N/A'}</td>
              <td className="w-1/4 text-right text-term-textDim">~20-30x</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Balance Sheet Summary */}
      <div className="grid grid-cols-4 gap-4">
        <div className="term-box p-3">
          <div className="text-term-textDim text-xs mb-1">Total Debt</div>
          <div className="text-lg font-semibold text-term-text">{formatCurrency(financials.totalDebt)}</div>
        </div>
        <div className="term-box p-3">
          <div className="text-term-textDim text-xs mb-1">Total Cash</div>
          <div className="text-lg font-semibold text-term-success">{formatCurrency(financials.totalCash)}</div>
        </div>
        <div className="term-box p-3">
          <div className="text-term-textDim text-xs mb-1">Net Debt</div>
          <div className={`text-lg font-semibold ${financials.netDebt < 0 ? 'text-term-success' : 'text-term-warning'}`}>
            {formatCurrency(financials.netDebt)}
          </div>
        </div>
        <div className="term-box p-3">
          <div className="text-term-textDim text-xs mb-1">Debt/Equity</div>
          <div className={`text-lg font-semibold ${financials.debtToEquity < 1 ? 'text-term-success' : financials.debtToEquity < 2 ? 'text-term-warning' : 'text-term-error'}`}>
            {financials.debtToEquity?.toFixed(2) || 'N/A'}x
          </div>
        </div>
      </div>
    </div>
  )
}

interface FreshnessIndicatorProps {
  freshness: DataFreshness
}

function FreshnessIndicator({ freshness }: FreshnessIndicatorProps) {
  const config = {
    live: { label: 'LIVE', color: 'text-term-success', bg: 'bg-term-success/20', icon: '●' },
    cached: { label: 'CACHED', color: 'text-term-warning', bg: 'bg-term-warning/20', icon: '○' },
    loading: { label: 'LOADING', color: 'text-term-info', bg: 'bg-term-info/20', icon: '◌' },
    error: { label: 'ERROR', color: 'text-term-error', bg: 'bg-term-error/20', icon: '✕' },
  }

  const { label, color, bg, icon } = config[freshness.source]

  return (
    <div className={`flex items-center gap-2 px-2 py-1 rounded ${bg}`}>
      <span className={color}>{icon}</span>
      <span className={`text-xs font-medium ${color}`}>{label}</span>
      {freshness.age && (
        <span className="text-xs text-term-textDim">({freshness.age})</span>
      )}
    </div>
  )
}

interface MetricCardProps {
  label: string
  value: string
  subValue?: string
  trend: 'up' | 'down' | 'stable'
}

function MetricCard({ label, value, subValue, trend }: MetricCardProps) {
  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <span className="text-term-success">↑</span>
      case 'down':
        return <span className="text-term-error">↓</span>
      default:
        return <span className="text-term-textDim">→</span>
    }
  }

  return (
    <div className="term-box p-3">
      <div className="text-term-textDim text-xs mb-1">{label}</div>
      <div className="text-xl font-semibold text-term-text flex items-center gap-2">
        {value} {getTrendIcon()}
      </div>
      {subValue && (
        <div className="text-xs text-term-textDim mt-1">{subValue}</div>
      )}
    </div>
  )
}
